import { fmtDateTime } from '../lib/dates.ts';
import { useEffect, useState } from 'react';
import { chat, directory, type ChatRoom, type Site, type Category }
  from '../lib/api.ts';
import { ChatRoom as Room } from './ChatRoom.tsx';
import { Modal } from './Modal';

/**
 * Past conversations.
 *
 * Filters by site, category, date and text, and returns sessions
 * rather than loose lines: one matched sentence out of context is how
 * a transcript gets misread months later.
 */
export const ChatHistory = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [results, setResults] = useState<ChatRoom[]>([]);
  const [open, setOpen] = useState<string>('');
  const [f, setF] = useState({
    siteId: '', categoryId: '', from: '', to: '', text: '',
  });

  useEffect(() => {
    directory.sites().then(setSites).catch(() => undefined);
    directory.categories().then(setCats).catch(() => undefined);
  }, []);

  const search = () => {
    chat.history(f).then(setResults).catch(() => setResults([]));
  };

  useEffect(search, []);

  const when = (iso: string): string =>
    fmtDateTime(iso);

  const set = (k: keyof typeof f) => (v: string) =>
    setF((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="noticeWrap">
      <div className="composer">
        <p className="mono">Find a conversation</p>
        <div className="noticeRow">
          <select
            value={f.siteId}
            onChange={(e) => set('siteId')(e.target.value)}
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <select
            value={f.categoryId}
            onChange={(e) => set('categoryId')(e.target.value)}
          >
            <option value="">All categories</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="noticeRow">
          <label className="check">
            From
            <input
              type="date"
              value={f.from}
              onChange={(e) => set('from')(e.target.value)}
            />
          </label>
          <label className="check">
            To
            <input
              type="date"
              value={f.to}
              onChange={(e) => set('to')(e.target.value)}
            />
          </label>
        </div>

        <input
          value={f.text}
          placeholder="Words in the conversation"
          onChange={(e) => set('text')(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
        />
        <button className="primary" onClick={search}>Search</button>
      </div>

      {results.length === 0 && (
        <p className="empty">No conversations match.</p>
      )}

      {results.map((r) => (
        <div key={r.id} className="composer">
          <button
            className="sentRow"
            onClick={() => setOpen(r.id)}
          >
            <span className="sentBody">
              <span className="catTag" style={{ background: r.color }} />
              {r.siteName} &middot; {r.categoryName}
            </span>
            <span className="mono sentCount">
              {when(r.openedAt)} &middot; {r.messageCount} messages
              <span className="sentGo">
                Read
              </span>
            </span>
          </button>

        </div>
      ))}

      {open && (() => {
        const r = results.find((x) => x.id === open);
        if (!r) return null;
        return (
          <Modal
            title={`${r.siteName} \u00b7 ${r.categoryName}`}
            subtitle={`${when(r.openedAt)} \u00b7 `
              + `${r.messageCount} messages`}
            onClose={() => setOpen('')}
            onPrint={() => window.print()}
          >
            <Room sessionId={r.id} initial={[]} staffPresent={[]}
                  readOnly />
          </Modal>
        );
      })()}
    </div>
  );
};
